/**
 * Referral Service
 * Handles referral code management, validation, and tracking
 */

import api from './api';
import logger from '../utils/logger';

const REFERRAL_STORAGE_KEY = 'rely_referral_code';
const REFERRAL_CAPTURE_VERSION = 1;
const REFERRAL_ATTRIBUTION_DAYS = 30;
const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;

class ReferralService {
  constructor() {
    this.endpoints = {
      code: '/api/user/referrals/code',
      stats: '/api/user/referrals/stats',
      list: '/api/user/referrals',
      validate: '/api/public/referrals/validate',
      payoutProfile: '/api/user/referrals/payout-profile',
    };
  }

  /**
   * Get or create the user's referral code
   * The auth token is injected automatically by the Axios interceptor in api.js.
   * @returns {Promise<Object>} Referral code response
   */
  async getReferralCode() {
    try {
      logger.info('Getting referral code');

      const response = await api.get(this.endpoints.code);
      return response.data;
    } catch (error) {
      logger.error('Error getting referral code', error);
      throw error;
    }
  }

  /**
   * Get referral statistics for the current user
   * @returns {Promise<Object>} Stats response
   */
  async getReferralStats() {
    try {
      logger.info('Getting referral stats');

      const response = await api.get(this.endpoints.stats);
      return response.data;
    } catch (error) {
      logger.error('Error getting referral stats', error);
      throw error;
    }
  }

  /**
   * Get paginated list of user's referrals
   * @param {number} page - Page number (0-indexed)
   * @param {number} limit - Items per page
   * @returns {Promise<Object>} Referrals list response
   */
  async getReferrals(page = 0, limit = 20) {
    try {
      logger.info('Getting referrals list', { page, limit });

      const response = await api.get(
        `${this.endpoints.list}?page=${page}&size=${limit}`
      );
      return response.data;
    } catch (error) {
      logger.error('Error getting referrals', error);
      throw error;
    }
  }

  /**
   * Validate a referral code (public endpoint, no auth required)
   * @param {string} code - Referral code to validate
   * @returns {Promise<Object>} Validation response
   */
  async validateCode(code) {
    try {
      logger.info('Validating referral code', { code });

      const response = await api.post(this.endpoints.validate, { code });
      // Always return the response body, even for validation failures
      return response.data;
    } catch (error) {
      logger.error('Error validating referral code', error);
      return {
        valid: false,
        message: 'Error al validar el código',
      };
    }
  }

  /**
   * Store referral code in localStorage (for use after Auth0 callback)
   * @param {string} code - Referral code
   * @param {Date} capturedAt - Attribution capture time
   */
  storeReferralCode(code, capturedAt = new Date()) {
    const normalizedCode =
      typeof code === 'string' ? code.toUpperCase().trim() : '';
    const capturedAtDate =
      capturedAt instanceof Date ? capturedAt : new Date(capturedAt);
    if (!normalizedCode || Number.isNaN(capturedAtDate.getTime())) return;

    const expiresAt = new Date(
      capturedAtDate.getTime() +
        REFERRAL_ATTRIBUTION_DAYS * DAY_IN_MILLISECONDS
    );
    localStorage.setItem(
      REFERRAL_STORAGE_KEY,
      JSON.stringify({
        version: REFERRAL_CAPTURE_VERSION,
        code: normalizedCode,
        capturedAt: capturedAtDate.toISOString(),
        expiresAt: expiresAt.toISOString(),
      })
    );
    logger.info('Stored referral code', { code: normalizedCode });
  }

  async getPayoutProfile() {
    try {
      const response = await api.get(this.endpoints.payoutProfile);
      return response.data;
    } catch (error) {
      logger.error('Error getting referral payout profile', error);
      throw error;
    }
  }

  async updatePayoutProfile(profile) {
    try {
      const response = await api.put(this.endpoints.payoutProfile, profile);
      return response.data;
    } catch (error) {
      logger.error('Error updating referral payout profile', error);
      throw error;
    }
  }

  /**
   * Get stored referral code from localStorage
   * @param {Date} now - Current time, injectable for deterministic tests
   * @returns {string|null} Stored referral code or null
   */
  getStoredReferralCode(now = new Date()) {
    return this.getStoredReferralCapture(now)?.code || null;
  }

  /**
   * Get the valid referral capture and its attribution timestamp.
   * Legacy raw-string values are discarded because their age is unknowable.
   * @param {Date} now - Current time, injectable for deterministic tests
   * @returns {{code: string, capturedAt: string, expiresAt: string}|null}
   */
  getStoredReferralCapture(now = new Date()) {
    const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
    if (!stored) return null;

    let capture;
    try {
      capture = JSON.parse(stored);
    } catch (error) {
      this.clearStoredReferralCode();
      return null;
    }

    const nowDate = now instanceof Date ? now : new Date(now);
    const capturedAt = new Date(capture?.capturedAt);
    const expiresAt = new Date(capture?.expiresAt);
    const isValid =
      capture?.version === REFERRAL_CAPTURE_VERSION &&
      typeof capture?.code === 'string' &&
      capture.code.trim().length > 0 &&
      !Number.isNaN(nowDate.getTime()) &&
      !Number.isNaN(capturedAt.getTime()) &&
      !Number.isNaN(expiresAt.getTime()) &&
      capturedAt.getTime() <= nowDate.getTime() &&
      expiresAt.getTime() > capturedAt.getTime() &&
      expiresAt.getTime() - capturedAt.getTime() <=
        REFERRAL_ATTRIBUTION_DAYS * DAY_IN_MILLISECONDS &&
      nowDate.getTime() < expiresAt.getTime();

    if (!isValid) {
      this.clearStoredReferralCode();
      return null;
    }

    return {
      code: capture.code.toUpperCase().trim(),
      capturedAt: capturedAt.toISOString(),
      expiresAt: expiresAt.toISOString(),
    };
  }

  /**
   * Clear stored referral code from localStorage
   */
  clearStoredReferralCode() {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
    logger.info('Cleared stored referral code');
  }

  /**
   * Copy referral code to clipboard
   * @param {string} code - Referral code to copy
   * @returns {Promise<boolean>} Success status
   */
  async copyCodeToClipboard(code) {
    try {
      await navigator.clipboard.writeText(code);
      return true;
    } catch (error) {
      logger.error('Failed to copy to clipboard', error);
      return false;
    }
  }

  /**
   * Generate share URL for a referral code
   * @param {string} code - Referral code
   * @param {string} baseUrl - Base URL (defaults to window.location.origin)
   * @returns {string} Share URL
   */
  getShareUrl(code, baseUrl = window.location.origin) {
    const origin = baseUrl.replace(/\/+$/, '');
    return `${origin}/auth/sign-up/default?ref=${encodeURIComponent(
      code.trim()
    )}`;
  }

  /**
   * Generate WhatsApp share URL
   * @param {string} code - Referral code
   * @param {string} message - Optional custom message
   * @returns {string} WhatsApp share URL
   */
  getWhatsAppShareUrl(code, message = null) {
    const defaultMessage = `Te comparto Rely para crear tu LLC en Estados Unidos. Usa mi enlace de referido: ${code}\n\n${this.getShareUrl(
      code
    )}`;
    const text = message || defaultMessage;
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  }

  /**
   * Generate email share URL
   * @param {string} code - Referral code
   * @returns {string} Mailto URL
   */
  getEmailShareUrl(code) {
    const subject = 'Crea tu LLC en USA con Rely';
    const body = `¡Hola!\n\nQuiero compartirte Rely para crear tu LLC en Estados Unidos.\n\nUsa mi enlace de referido: ${code}\n\n${this.getShareUrl(
      code
    )}\n\n¡Saludos!`;
    return `mailto:?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  }
}

const referralService = new ReferralService();

export default referralService;
